import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isPropertiesDropdownOpen, setIsPropertiesDropdownOpen] = useState(false);
  const { currency, setCurrency } = useCurrency();
  const { language, setLanguage, t, availableLanguages } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: t('HOME'), href: '/' },
    {
      name: t('ABOUT'),
      href: '/about',
      subItems: [
        { name: t('ABOUT_US'), href: '/about' },
        { name: t('OUR_TEAM'), href: '/team' },
        { name: t('HOW_IT_WORKS'), href: '/how-it-works' }
      ]
    },
    {
      name: t('PROPERTIES'),
      href: '/properties',
      subItems: [
        { name: t('ALL_PROPERTIES'), href: '/properties' },
        { name: t('GALLERY'), href: '/gallery' }
      ]
    },
    { name: t('FAQ'), href: '/faq' },
    { name: t('BLOG'), href: '/blog' },
    { name: t('CONTACT'), href: '/contact' }
  ];

  const currencyOptions = [
    { code: 'USD', label: t('US_DOLLAR'), symbol: '$', flag: '🇺🇸' },
    { code: 'EUR', label: t('EURO'), symbol: '€', flag: '🇪🇺' },
    { code: 'GBP', label: t('POUND_STERLING'), symbol: '£', flag: '🇬🇧' },
    { code: 'UGX', label: t('UGANDAN_SHILLING'), symbol: 'USh', flag: '🇺🇬' },
  ];

  // Check current page
  const isHomePage = window.location.pathname === '/';
  const isDetailPage = window.location.pathname.includes('/blog/') || window.location.pathname.includes('/property/');
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || isDetailPage
        ? 'bg-opacity-95 backdrop-blur-sm shadow-lg'
        : 'bg-transparent'
    }`} style={{
      ...(isScrolled || isDetailPage ? { backgroundColor: '#81be00' } : {})
    }}>
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-6 text-[11px] md:text-xs">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isScrolled || isDetailPage ? 'py-3' : 'py-4'
        }`}>
          {/* Left Utilities */}
          <div className="hidden lg:flex items-center gap-2 lg:mr-6 xl:mr-10 text-[10px] md:text-[11px]">
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`rounded-full px-2.5 py-1 uppercase tracking-wide text-[10px] md:text-[11px] font-semibold border transition-all duration-300 flex items-center gap-1.5 ${
                  isScrolled || isDetailPage
                    ? 'bg-white text-[#1a3a0a] border-white hover:bg-white/90'
                    : 'bg-white/20 text-white border-white/30 hover:bg-white/40'
                }`}
              >
                {currency} <ChevronDown size={16} className="opacity-80" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-stone-200 shadow-lg rounded-lg p-2 min-w-[220px]">
                {currencyOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.code}
                    onSelect={() => setCurrency(opt.code)}
                    className="flex items-center gap-2 text-stone-800 cursor-pointer rounded-md px-3 py-2 focus:bg-stone-100"
                  >
                    <span className="text-lg">{opt.flag}</span>
                    <span className="font-semibold">{opt.code}</span>
                    <span className="text-xs text-stone-500">{opt.label}</span>
                    <span className="ml-auto text-stone-500">{opt.symbol}</span>
                    {currency === opt.code && <span className="text-[#81be00] ml-2">•</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                className={`rounded-full px-2.5 py-1 uppercase tracking-wide text-[10px] md:text-[11px] font-semibold border transition-all duration-300 flex items-center gap-1.5 ${
                  isScrolled || isDetailPage
                    ? 'bg-white text-[#1a3a0a] border-white hover:bg-white/90'
                    : 'bg-white/20 text-white border-white/30 hover:bg-white/40'
                }`}
              >
                LANG <ChevronDown size={16} className="opacity-80" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-stone-200 shadow-lg rounded-lg p-2 min-w-[200px]">
                {availableLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onSelect={() => setLanguage(lang.code)}
                    className="flex items-center gap-2 text-stone-800 cursor-pointer rounded-md px-3 py-2 focus:bg-stone-100"
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-semibold">{lang.name}</span>
                    {language === lang.code && <span className="text-[#81be00] ml-auto">•</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href="/request-viewing"
              className={`inline-block transition-all duration-300 px-4 py-2 uppercase tracking-wide text-[11px] md:text-xs font-semibold rounded-full border ${
                isScrolled || isDetailPage
                  ? 'bg-white text-[#1a3a0a] border-white hover:bg-white/90 hover:text-[#4c8b14]'
                  : 'bg-[#81be00] text-white border-[#81be00] hover:bg-[#6a9e00] hover:text-white'
              }`}
            >
              {t('REQUEST_A_VIEWING')}
            </a>
          </div>

          {/* Main Navigation */}
          <div className="hidden lg:flex items-center space-x-5 xl:space-x-6 lg:ml-6 xl:ml-8 text-[11px] md:text-xs">
            {menuItems.map((item) => (
              item.subItems ? (
                <DropdownMenu
                  key={item.name}
                  open={item.name === t('ABOUT') ? isAboutDropdownOpen : item.name === t('PROPERTIES') ? isPropertiesDropdownOpen : undefined}
                  onOpenChange={item.name === t('ABOUT') ? setIsAboutDropdownOpen : item.name === t('PROPERTIES') ? setIsPropertiesDropdownOpen : undefined}
                >
                  <DropdownMenuTrigger
                    className={`transition-colors duration-300 font-medium tracking-wide flex items-center ${
                      isScrolled || isDetailPage
                        ? 'text-white hover:text-white/80'
                        : 'text-white hover:text-[#81be00]'
                    }`}
                    onMouseEnter={() => (item.name === t('ABOUT') ? setIsAboutDropdownOpen(true) : item.name === t('PROPERTIES') ? setIsPropertiesDropdownOpen(true) : null)}
                    onMouseLeave={() => (item.name === t('ABOUT') ? setIsAboutDropdownOpen(false) : item.name === t('PROPERTIES') ? setIsPropertiesDropdownOpen(false) : null)}
                  >
                    {item.name}
                    <ChevronDown size={16} className={`ml-1 transition-transform duration-300 ${(item.name === t('ABOUT') ? isAboutDropdownOpen : isPropertiesDropdownOpen) ? 'rotate-180' : ''}`} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-[#81be00] border border-[#81be00] shadow-lg rounded-md p-4 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-300"
                    onMouseEnter={() => (item.name === t('ABOUT') ? setIsAboutDropdownOpen(true) : setIsPropertiesDropdownOpen(true))}
                    onMouseLeave={() => (item.name === t('ABOUT') ? setIsAboutDropdownOpen(false) : setIsPropertiesDropdownOpen(false))}
                  >
                    {item.subItems.map((subItem) => (
                      <DropdownMenuItem key={subItem.name} asChild className="hover:bg-white/20">
                        <a href={subItem.href} className="cursor-pointer text-white hover:text-white/80 w-full block px-3 py-2">
                          {subItem.name}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className={`transition-colors duration-300 font-medium tracking-wide ${
                    isScrolled || isDetailPage
                      ? 'text-white hover:text-white/80'
                      : 'text-white hover:text-[#81be00]'
                  }`}
                >
                  {item.name}
                </a>
              )
            ))}
          </div>

          {/* Logo */}
          <div className="flex-1 lg:flex-none flex justify-center lg:justify-end lg:mx-8 xl:mx-12">
            <a href="/" className="flex items-center">
              <img
                src="https://ik.imagekit.io/hqhiltiie/WhatsApp%20Image%202025-10-10%20at%2019.44.26.jpeg?updatedAt=1760182338495"
                alt="Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto object-contain"
              />
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`transition-colors duration-300 ${
                isScrolled || isDetailPage
                  ? 'text-white hover:text-white/80'
                  : 'text-white hover:text-[#81be00]'
              }`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white backdrop-blur-sm p-4 mt-2 rounded-lg">
            <div className="flex flex-col space-y-4">
              <a
                href="/request-viewing"
                className={`inline-block transition-all duration-300 px-6 py-2 uppercase tracking-wide text-xs text-center font-semibold rounded-full border ${
                  isScrolled || isDetailPage
                    ? 'bg-white text-[#1a3a0a] border-white hover:bg-white/90 hover:text-[#4c8b14]'
                    : 'bg-[#81be00] text-white border-[#81be00] hover:bg-white hover:text-[#81be00]'
                }`}
              >
                {t('REQUEST_A_VIEWING')}
              </a>
              <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-stone-800">{t('CURRENCY')}</span>
                <div className="flex items-center gap-2">
                  {currencyOptions.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => {
                        setCurrency(opt.code);
                        setIsMenuOpen(false);
                      }}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 flex items-center gap-1 ${
                        currency === opt.code
                          ? 'bg-[#81be00] text-stone-900 border-[#81be00]'
                          : 'text-stone-700 border-stone-200 hover:border-stone-400'
                      }`}
                    >
                      <span>{opt.flag}</span>
                      <span>{opt.code}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-stone-800">{t('LANGUAGE')}</span>
                <div className="flex items-center gap-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsMenuOpen(false);
                      }}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors duration-200 flex items-center gap-1 ${
                        language === lang.code
                          ? 'bg-[#81be00] text-stone-900 border-[#81be00]'
                          : 'text-stone-700 border-stone-200 hover:border-stone-400'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>
              {menuItems.map((item) => (
                <div key={item.name}>
                  {item.subItems ? (
                    <>
                      <a
                        href={item.href}
                        className="text-stone-800 hover:text-[#81be00] transition-colors duration-300 text-xs md:text-sm font-medium tracking-wide py-3 block"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </a>
                      {item.subItems.map((subItem) => (
                        <a
                          key={subItem.name}
                          href={subItem.href}
                          className="text-stone-800 hover:text-[#81be00] transition-colors duration-300 text-xs md:text-sm font-medium tracking-wide py-3 block ml-4"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </a>
                      ))}
                    </>
                  ) : (
                    <a
                      href={item.href}
                      className="text-white hover:text-[#81be00] transition-colors duration-300 text-xs md:text-sm font-medium tracking-wide py-3 block"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
